import type {Theme} from '@emotion/react';

interface VariablesProps {
    theme: Theme;
}

/**
 * Generates CSS variables for the base state of SearchField component.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The emotion theme object with color definitions.
 * @returns CSS string with CSS variables for light and dark themes, including error states.
 */
export const getBaseVariables = ({theme}: VariablesProps) => `
    &[data-theme=light] {
        --bg-color: ${theme.colors.neutral0};
        --border-color: ${theme.colors.neutral0};
        --font-color: ${theme.colors.textPrimary};
        --icon-color: ${theme.colors.textSecondary};
    }
    &[data-theme=light][data-has-error=true] {
        --bg-color: ${theme.colors.neutral0};
        --border-color: ${theme.colors.error};
        --font-color: ${theme.colors.error};
        --icon-color: ${theme.colors.error};
    }
    &[data-theme=dark] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.neutral20};
        --font-color: ${theme.colors.textPrimary};
        --icon-color: ${theme.colors.textSecondary};
    }
    &[data-theme=dark][data-has-error=true] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.error};
        --font-color: ${theme.colors.error};
        --icon-color: ${theme.colors.error};
    }
`;

/**
 * Generates CSS variables for the disabled state of SearchField component.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The emotion theme object with color definitions.
 * @returns CSS string with CSS variables for disabled state in light and dark themes, including error states.
 */
export const getDisabledVariables = ({theme}: VariablesProps) => `
    &[data-theme=light] {
        --bg-color: ${theme.colors.neutral0};
        --border-color: ${theme.colors.neutral0};
        --font-color: ${theme.colors.textPrimary};
        --icon-color: ${theme.colors.textSecondary};
    }
    &[data-theme=light][data-has-error=true] {
        --bg-color: ${theme.colors.neutral0};
        --border-color: ${theme.colors.error};
        --font-color: ${theme.colors.error};
        --icon-color: ${theme.colors.error};
    }
    &[data-theme=dark] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.neutral20};
        --font-color: ${theme.colors.textPrimary};
        --icon-color: ${theme.colors.textSecondary};
    }
    &[data-theme=dark][data-has-error=true] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.error};
        --font-color: ${theme.colors.error};
        --icon-color: ${theme.colors.error};
    }
`;

/**
 * Generates CSS variables for the hover state of SearchField component.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The emotion theme object with color definitions.
 * @returns CSS string with CSS variables for hover state in light and dark themes, including error states.
 */
export const getHoverVariables = ({theme}: VariablesProps) => `
    &[data-theme=light] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.neutral0};
        --font-color: ${theme.colors.textPrimary};
        --icon-color: ${theme.colors.primaryDark};
    }
    &[data-theme=light][data-has-error=true] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.error};
        --font-color: ${theme.colors.error};
        --icon-color: ${theme.colors.error};
    }
    &[data-theme=dark] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.neutral20};
        --font-color: ${theme.colors.textPrimary};
        --icon-color: ${theme.colors.primaryDark};
    }
    &[data-theme=dark][data-has-error=true] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.error};
        --font-color: ${theme.colors.error};
        --icon-color: ${theme.colors.error};
    }
`;

/**
 * Generates CSS variables for the focus state of SearchField component.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The emotion theme object with color definitions.
 * @returns CSS string with CSS variables for focus state in light and dark themes, including error states.
 */
export const getFocusVariables = ({theme}: VariablesProps) => `
    &[data-theme=light] {
        --bg-color: ${theme.colors.neutral0};
        --border-color: ${theme.colors.primaryDark};
        --font-color: ${theme.colors.textPrimary};
        --icon-color: ${theme.colors.primaryDark};
    }
    &[data-theme=light][data-has-error=true] {
        --bg-color: ${theme.colors.neutral0};
        --border-color: ${theme.colors.error};
        --font-color: ${theme.colors.error};
        --icon-color: ${theme.colors.error};
    }
    &[data-theme=dark] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.primaryDark};
        --font-color: ${theme.colors.textPrimary};
        --icon-color: ${theme.colors.primaryDark};
    }
    &[data-theme=dark][data-has-error=true] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.error};
        --font-color: ${theme.colors.error};
        --icon-color: ${theme.colors.error};
    }
`;